/*!
 * light-storage v1.1.3
 * (c) 2019-2020 xunmi
 * Released under the MIT License.
 */

var version = "1.1.3";

var isObject = function (val) { return val !== null && typeof val === 'object'; };
var isNumber = function (val) { return typeof val === 'number'; };
var startsWith = function (str, target) { return str.slice(0, target.length) === target; };
var hasOwnProperty = Object.prototype.hasOwnProperty;
var hasOwn = function (obj, key) { return hasOwnProperty.call(obj, key); };

/*!
 * event-channel v1.0.1
 * (c) 2020 xunmi
 * Released under the MIT License.
 */

var version$1 = "1.0.1";

var CustomMap = /** @class */ (function () {
    function CustomMap() {
        this._data = Object.create(null);
    }
    CustomMap.prototype.delete = function (key) {
        this._data[key] = undefined;
    };
    CustomMap.prototype.get = function (key) {
        return this._data[key];
    };
    CustomMap.prototype.has = function (key) {
        return this._data[key] !== undefined;
    };
    CustomMap.prototype.set = function (key, value) {
        this._data[key] = value;
    };
    return CustomMap;
}());
var createMap = function () {
    // if support `Symbol`, definitely support `Map`.
    // use `Map` to save `Symbol`
    var isSupport = typeof Map !== 'undefined';
    if (isSupport)
        return new Map();
    // not support `Symbol`
    else
        return new CustomMap();
};

// IE compatible
var findIndex = function (arr, func) {
    var k = 0;
    while (k < arr.length) {
        if (func.call(arr, arr[k], k))
            return k;
        k++;
    }
    return -1;
};
var isArray = Array.isArray;

/** @public */
var EventChannel = /** @class */ (function () {
    /**
     * @param [options.before] - cache previous (offline) events
     */
    function EventChannel(options) {
        var _a;
        this.subscribers = createMap();
        this.historyParams = createMap();
        this.before = (_a = options === null || options === void 0 ? void 0 : options.before) !== null && _a !== void 0 ? _a : false;
    }
    EventChannel.prototype.canCache = function (key) {
        if (this.before === true)
            return true;
        return isArray(this.before) && this.before.indexOf(key) > -1;
    };
    EventChannel.prototype.invokeSub = function (subscriber, context, params) {
        return subscriber.apply(context, params);
    };
    EventChannel.prototype.addSub = function (key, subscriber, context) {
        if (!this.subscribers.has(key))
            this.subscribers.set(key, []);
        var list = this.subscribers.get(key);
        // deduplication
        var index = findIndex(list, function (v) { return v[0] === subscriber && v[1] === context; });
        if (index < 0)
            list.push([subscriber, context]);
        else
            list.splice(index, 1, [subscriber, context]);
    };
    /**
     * subscribe to an event
     * @param key - event name
     * @param subscriber
     * @param options
     * @param [options.before] - whether to subscribe to previous events.
     * The premise is to use `new EventChannel({ before: true | [key] })`
     * @param [options.context] - context of subscriber
     */
    EventChannel.prototype.on = function (key, subscriber, options) {
        var _this = this;
        var _a;
        var _b = options !== null && options !== void 0 ? options : {}, before = _b.before, context = _b.context;
        this.addSub(key, subscriber, context);
        if (before) {
            (_a = this.historyParams.get(key)) === null || _a === void 0 ? void 0 : _a.forEach(function (v) { return _this.invokeSub(subscriber, context, v); });
        }
    };
    /**
     * Subscribe to a one-time event
     * @param key - event name
     * @param subscriber
     * @param options
     * @param [options.before] - whether to subscribe to previous events.
     * The premise is to use `new EventChannel({ before: true | [key] })`
     * @param [options.context] - context of subscriber
     */
    EventChannel.prototype.once = function (key, subscriber, options) {
        var _this = this;
        var _a = options !== null && options !== void 0 ? options : {}, before = _a.before, context = _a.context;
        if (before) {
            var history_1 = this.historyParams.get(key);
            if (isArray(history_1) && history_1.length) {
                this.invokeSub(subscriber, context, history_1[0]);
                return;
            }
        }
        var wrapper = function () {
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            _this.off(key, wrapper);
            return _this.invokeSub(subscriber, context, args);
        };
        this.addSub(key, wrapper, context);
    };
    /**
     * Unsubscribe to an event
     * @param key - event name
     * @param subscriber
     */
    EventChannel.prototype.off = function (key, subscriber) {
        var subscribers = this.subscribers.get(key);
        if (!subscribers)
            return;
        if (subscriber) {
            var index = findIndex(subscribers, function (v) { return v[0] === subscriber; });
            subscribers.splice(index, 1);
        }
        else {
            this.subscribers.delete(key);
        }
    };
    /**
     * Dispatch an event
     * @param key - event name
     * @param params - event parameters
     */
    EventChannel.prototype.emit = function (key) {
        var _this = this;
        var _a;
        var params = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            params[_i - 1] = arguments[_i];
        }
        if (this.canCache(key)) {
            if (!this.historyParams.has(key))
                this.historyParams.set(key, []);
            this.historyParams.get(key).push(params);
        }
        (_a = this.subscribers.get(key)) === null || _a === void 0 ? void 0 : _a.forEach(function (_a) {
            var sub = _a[0], ctx = _a[1];
            return _this.invokeSub(sub, ctx, params);
        });
    };
    EventChannel.version = version$1;
    return EventChannel;
}());

var __spreadArrays = (window && window.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
var Subject = /** @class */ (function () {
    function Subject() {
        this.event = new EventChannel();
    }
    Subject.prototype.notify = function (key) {
        var _a;
        var rest = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            rest[_i - 1] = arguments[_i];
        }
        if (rest[0] !== rest[1])
            (_a = this.event).emit.apply(_a, __spreadArrays([key], rest));
    };
    /**
     * Watch the change of a value associated with the given key
     * @param key
     * @param observer
     * @since v1.1.0
     */
    Subject.prototype.watch = function (key, observer) {
        this.event.on(key, observer);
    };
    /**
     * Unwatch that stops firing the observer.
     * @param key
     * @param observer
     * @since v1.1.0
     */
    Subject.prototype.unwatch = function (key, observer) {
        this.event.off(key, observer);
    };
    return Subject;
}());
var SubjectItem = /** @class */ (function () {
    function SubjectItem() {
    }
    /**
     * Watch the change of a value
     * @param observer
     * @since v1.1.0
     */
    SubjectItem.prototype.watch = function (observer) {
        this.instance.watch(this.key, observer);
        return this.context;
    };
    /**
     * Unwatch that stops firing the observer.
     * @param observer
     * @since v1.1.0
     */
    SubjectItem.prototype.unwatch = function (observer) {
        this.instance.unwatch(this.key, observer);
        return this.context;
    };
    return SubjectItem;
}());

var __extends = (window && window.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var LightStorageItem = /** @class */ (function (_super) {
    __extends(LightStorageItem, _super);
    function LightStorageItem(instance, key) {
        var _a;
        var _this = _super.call(this) || this;
        _this.instance = instance;
        _this.key = key;
        _this.context = _this;
        // @ts-ignore
        _this.data = (_a = _this.instance.handleExpired(_this.key)) !== null && _a !== void 0 ? _a : {};
        return _this;
    }
    Object.defineProperty(LightStorageItem.prototype, "value", {
        get: function () {
            return this.data.value;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LightStorageItem.prototype, "maxAge", {
        get: function () {
            return this.data.maxAge;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LightStorageItem.prototype, "time", {
        /**
         * Return the created time
         */
        get: function () {
            return this.data.time;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Set the value with the given key
     * Note: Will keep if original storage has maxAge
     * @param value
     */
    LightStorageItem.prototype.setValue = function (value) {
        this.instance.set(this.key, value, { maxAge: this.maxAge, update: false });
        this.data.value = value;
        return this.context;
    };
    LightStorageItem.prototype.setMaxAge = function (maxAge) {
        this.instance.set(this.key, this.value, { maxAge: maxAge, update: false });
        this.data.maxAge = maxAge;
        return this.context;
    };
    /**
     * Update creation time
     */
    LightStorageItem.prototype.update = function () {
        this.instance.set(this.key, this.value, { maxAge: this.maxAge, update: true });
        this.data.time = this.instance.getCreatedTime(this.key);
        return this.context;
    };
    /**
     * Remove oneself
     */
    LightStorageItem.prototype.remove = function () {
        this.instance.remove(this.key);
        this.data = {};
        return this.context;
    };
    return LightStorageItem;
}(SubjectItem));

var _a;
var CustomSet = /** @class */ (function () {
    function CustomSet(list) {
        if (list === void 0) { list = []; }
        this.data = list.slice();
    }
    Object.defineProperty(CustomSet.prototype, "size", {
        get: function () {
            return this.data.length;
        },
        enumerable: false,
        configurable: true
    });
    CustomSet.prototype.add = function (value) {
        if (this.has(value))
            return;
        this.data.push(value);
    };
    CustomSet.prototype.delete = function (value) {
        var index = this.data.indexOf(value);
        if (index < 0)
            return false;
        this.data.splice(index, 1);
        return true;
    };
    CustomSet.prototype.clear = function () {
        this.data = [];
    };
    CustomSet.prototype.has = function (value) {
        return this.data.indexOf(value) > -1;
    };
    CustomSet.prototype.forEach = function (func) {
        var _this = this;
        this.data.forEach(function (value) { return func.call(_this, value); });
    };
    return CustomSet;
}());
var List = (_a = window.Set) !== null && _a !== void 0 ? _a : CustomSet;

var __extends$1 = (window && window.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
/**
 * LightStorage
 * @public
 */
var LightStorage = /** @class */ (function (_super) {
    __extends$1(LightStorage, _super);
    /**
     * @param prefix - Custom prefix of Storage
     */
    function LightStorage(prefix) {
        if (prefix === void 0) { prefix = 'light-storage'; }
        var _this = _super.call(this) || this;
        _this.localStorage = window.localStorage;
        _this._prefix = prefix;
        _this.timers = Object.create(null);
        _this.reload();
        return _this;
    }
    Object.defineProperty(LightStorage.prototype, "prefix", {
        /**
         * Get or set the current prefix
         */
        get: function () {
            return this._prefix;
        },
        set: function (value) {
            var _this = this;
            this.reload();
            var len = this._prefix.length;
            this._keys.forEach(function (key) {
                var newKey = String(value) + key.slice(len);
                var origin = _this.localStorage.getItem(key);
                _this.localStorage.removeItem(key);
                /* istanbul ignore else */
                if (origin != null)
                    _this.localStorage.setItem(newKey, origin);
            });
            this._prefix = value;
            this.collectKeys();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(LightStorage.prototype, "keys", {
        /**
         * Get all keys currently present
         */
        get: function () {
            var _this = this;
            this.reload();
            var keys = [];
            this._keys.forEach(function (k) { return keys.push(_this.getSimplifyKey(k)); });
            return keys;
        },
        enumerable: false,
        configurable: true
    });
    LightStorage.prototype.collectKeys = function () {
        var _this = this;
        var _keys = Object.keys(this.localStorage).filter(function (key) { return startsWith(key, _this._prefix); });
        this._keys = new List(_keys);
    };
    LightStorage.isFormSelf = function (data) {
        return isObject(data) && hasOwn(data, 'version');
    };
    LightStorage.prototype.getCompleteData = function (key) {
        var origin = this.localStorage.getItem(key);
        // if use `localStorage.removeItem`, remove the key
        if (origin == null) {
            this._keys.delete(key);
            return;
        }
        try {
            var data = JSON.parse(origin);
            return LightStorage.isFormSelf(data) ? data : { value: data };
        }
        catch (_a) {
            return { value: origin };
        }
    };
    LightStorage.prototype.getCompleteKey = function (key) {
        if (startsWith(key, this._prefix))
            return key;
        return this._prefix + "-" + key;
    };
    LightStorage.prototype.getSimplifyKey = function (key) {
        return key.slice(this._prefix.length + 1);
    };
    /** @internal  */
    LightStorage.prototype.handleExpired = function (key) {
        key = this.getCompleteKey(key);
        this._keys.add(key);
        var data = this.getCompleteData(key);
        if (!data)
            return;
        var time = data.time, maxAge = data.maxAge;
        // if it has expiration
        if (isNumber(maxAge) && isNumber(time)) {
            var isValid = LightStorage.isValid(time, maxAge);
            // if it within the validity period, start timer
            if (isValid) {
                if (!this.timers[key])
                    this.startTimer(key, time, maxAge);
            }
            else {
                // expired
                this.remove(key);
                return;
            }
        }
        return data;
    };
    /**
     * use timer for expiration
     */
    LightStorage.prototype.startTimer = function (key, time, maxAge) {
        var _this = this;
        var remaining = time + maxAge - Date.now();
        this.timers[key] = window.setTimeout(function () { return _this.remove(key); }, remaining);
    };
    LightStorage.prototype.abortTimer = function (key) {
        if (this.timers[key]) {
            window.clearTimeout(this.timers[key]);
            this.timers[key] = undefined;
        }
    };
    LightStorage.isValid = function (time, maxAge) {
        var age = Date.now() - time;
        return age >= 0 && age <= maxAge;
    };
    /**
     * Synchronize data in the localStorage, and check validity
     * @remarks use for localStorage is used directly, and it does not meet expectations
     */
    LightStorage.prototype.reload = function () {
        var _this = this;
        this.collectKeys();
        this._keys.forEach(function (k) { return _this.handleExpired(k); });
    };
    /**
     * Set the value with the given key, creating a new value if none existed
     * Note: calling will reset the original storage
     * @param key - Key name
     * @param value - Data to be stored
     * @param options - Options
     */
    LightStorage.prototype.set = function (key, value, options) {
        var _a;
        key = this.getCompleteKey(key);
        var data = { value: value, version: LightStorage.version };
        var _b = options !== null && options !== void 0 ? options : {}, maxAge = _b.maxAge, update = _b.update;
        var now = Date.now();
        var oldData = this.handleExpired(key);
        data.time = update ? now : (_a = oldData === null || oldData === void 0 ? void 0 : oldData.time) !== null && _a !== void 0 ? _a : now;
        this.abortTimer(key);
        if (maxAge) {
            if (!isNumber(maxAge) || maxAge < 0) {
                throw new TypeError('maxAge is invalid, and must be a non-negative number');
            }
            data.maxAge = maxAge;
            this.startTimer(key, data.time, maxAge);
        }
        this.localStorage.setItem(key, JSON.stringify(data));
        this._keys.add(key);
        // notify `set` handler
        this.notify(this.getSimplifyKey(key), value, oldData === null || oldData === void 0 ? void 0 : oldData.value);
    };
    /**
     * Return the current value associated with the given key, or undefined if the given key does not exist.
     * @param key - Key name
     * @param defaultValue - Return the default value if the given key does not exist
     */
    LightStorage.prototype.get = function (key, defaultValue) {
        var data = this.handleExpired(key);
        return (data === null || data === void 0 ? void 0 : data.value) === undefined ? defaultValue : data.value;
    };
    /**
     * Return the created time associated with the given key
     */
    LightStorage.prototype.getCreatedTime = function (key) {
        var _a;
        return (_a = this.handleExpired(key)) === null || _a === void 0 ? void 0 : _a.time;
    };
    /**
     * Return the `maxAge` associated with the given key
     */
    LightStorage.prototype.getMaxAge = function (key) {
        var _a;
        return (_a = this.handleExpired(key)) === null || _a === void 0 ? void 0 : _a.maxAge;
    };
    /**
     * Whether it contains the key
     */
    LightStorage.prototype.has = function (key) {
        key = this.getCompleteKey(key);
        if (this._keys.has(key)) {
            return !!this.handleExpired(key);
        }
        return false;
    };
    /**
     * Remove the data with the given key
     */
    LightStorage.prototype.remove = function (key) {
        key = this.getCompleteKey(key);
        var data = this.getCompleteData(key);
        this.abortTimer(key);
        this.localStorage.removeItem(key);
        this._keys.delete(key);
        this.notify(this.getSimplifyKey(key), undefined, data === null || data === void 0 ? void 0 : data.value);
    };
    /**
     * Clear all data with the current prefix
     */
    LightStorage.prototype.clear = function () {
        var _this = this;
        this.collectKeys();
        this._keys.forEach(function (key) {
            var data = _this.getCompleteData(key);
            _this.abortTimer(key);
            _this.localStorage.removeItem(key);
            _this.notify(_this.getSimplifyKey(key), undefined, data === null || data === void 0 ? void 0 : data.value);
        });
        this._keys.clear();
    };
    /**
     * Generate an instance with the given key
     * @param key - the given key
     */
    LightStorage.prototype.select = function (key) {
        return new LightStorage.Item(this, key);
    };
    LightStorage.version = version;
    LightStorage.Item = LightStorageItem;
    return LightStorage;
}(Subject));

export default LightStorage;
